import React, { useEffect, useState } from 'react';
import {
    Card,
    Button,
    Tabs,
    Row,
    Col,
    Modal,
    Input,
    Select,
    Switch,
    Upload,
    Space,
    Typography,
    Badge
} from 'antd';
import {
    FileTextOutlined,
    CalendarOutlined,
    EditOutlined,
    DeleteOutlined,
    UploadOutlined,
    TeamOutlined,
    BellOutlined,
    ExclamationCircleFilled,
    DollarOutlined
} from '@ant-design/icons';
import Footer from '../../components/Footer';
import NavBar from '../../components/NavBar';
import SideBar from '../../components/SideBar';
import { useBreadcrumbTitle } from '../../contexts/BreadcrumbContext';
import axiosInstance from '../../variables/api/axiosInstance';
import { API_URL, LOGGED_USER, TOKEN } from '../../variables/constants';
import { useLocation, useNavigate } from 'react-router-dom';
import { frenchDate } from '../../variables/functions';
import ProfilingAgence from './details-agence/ProfilingAgence';
import AgenceStatsSummary from './details-agence/AgenceStatsSummary';
import UtilisateursAgenceTab from './details-agence/UtilisateursAgenceTab';
import SuiviAgenceTab from './details-agence/SuiviAgenceTab';
import AddAgenceModal from './details-membre/AddAgenceModal';
import { toast } from 'react-toastify';
import { rightsService } from '../../services/rightsService';
import TauxCommissionsAgenceModal from './details-agence/TauxCommissionsAgenceModal';
import { getCompagnieLogo } from '../../hooks/useFilesHooks';

const { Title, Text } = Typography;
const { confirm } = Modal;

// Couleurs de la charte
const PRIMARY_COLOR = '#242166';
const SECONDARY_COLOR = '#1890ff';
const BACKGROUND_LIGHT = '#f5f7fa';

const DetailsAgence = () => {
    // Onglet initial : pilotable via ?tab=suivi (deep-link depuis le menu / liens).
    const [activeTab, setActiveTab] = useState(() => {
        try { return new URLSearchParams(window.location.search).get('tab') || '1'; } catch (e) { return '1'; }
    });
    const { setPageTitle } = useBreadcrumbTitle();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [agence, setAgence] = useState("");
    const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
    const [logoImagePreviewUrl, setLogoImagePreviewUrl] = useState(null);
    const [utilisateurs, setUtilisateurs] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [globalStats, setGlobalStats] = useState({});
    const [membre, setMembre] = useState({});
    const [villes, setVilles] = useState([]);
    const [categories, setCategories] = useState([]);
    const [commissionValues, setCommissionValues] = useState({});
    const [manageTauxOpen, setManageTauxOpen] = useState(false);
    const [savingTaux, setSavingTaux] = useState(false);
    const [agreementId, setAgreementId] = useState(null);
    const [loadingAgreement, setLoadingAgreement] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (agence?.nom) setPageTitle(agence.nom);
        return () => setPageTitle(null);
    }, [agence]);

    const canManageAgences = rightsService("HPE033") || rightsService("HPE041") || (LOGGED_USER?.profil?.nom || "").toLowerCase().includes("admin");

    const extractAgreementId = (ag) => {
        if (!ag) return null;
        const valid = Array.isArray(ag.validAgrements) ? ag.validAgrements : [];
        const other = Array.isArray(ag.agrements) ? ag.agrements : [];
        return valid[0]?.agreementId || other[0]?.agreementId || null;
    };

    const handleAgenceSaved = (updatedAgence) => {
        if (updatedAgence) {
            setAgence(updatedAgence);
            setMembre(updatedAgence?.organisme || {});
        }
        setIsModalVisible(false);
        navigate(-1);
    };

    useEffect(() => {
        let code = "";
        const data = location?.state;
        code = data ? data?.codeAgence : LOGGED_USER?.agence?.codeAgence;
        let codeOrg = data ? data?.codeOrganisme : LOGGED_USER.agence?.organisme?.code;

        axiosInstance
            .get(`/api/agence/organisme/code/${code}/${codeOrg}`).then((res) => {
                console.log(res.data);
                if (res.data?.signature) {
                    fetch(
                        `${API_URL}/api/agence/signature/${res.data?.signature.split("//")[2]}`,
                        {
                            headers: {
                                Authorization: `Bearer ${TOKEN}`,
                                "Content-Type": "application/json",
                                "Access-Control-Allow-Origin": "*",
                                "Access-Control-Allow-Methods":
                                    "GET, PUT, POST, DELETE, OPTIONS",
                                "Access-Control-Allow-Headers":
                                    "Content-Type, Access-Control-Allow-Headers, Authorization, X-Requested-With",
                            },
                        }
                    )
                        .then(async (response) => {
                            // console.log("The response", response);

                            // convert byte to file
                            const imageBytes = await response.arrayBuffer();
                            const blob = new Blob([imageBytes], { type: "image/png" });
                            const imageUrl = URL.createObjectURL(blob);
                            setImagePreviewUrl(imageUrl);
                        })
                        .catch((error) => {
                            console.log(error);
                        });
                }

                if (res.data?.organisme?.logo) {
                    getCompagnieLogo(res.data?.organisme?.code)
                        .then(async (response) => {
                            const imageBytes = await response.arrayBuffer();
                            const blob = new Blob([imageBytes], { type: "image/png" });
                            const imageUrl = URL.createObjectURL(blob);
                            setLogoImagePreviewUrl(imageUrl);
                        })
                        .catch((error) => {
                            console.log(error);
                        });
                }

                setAgence(res.data);
                setMembre(res.data?.organisme || {});
                setAgreementId(extractAgreementId(res.data));
            }).catch(err => {
                console.log(err);
            });


        axiosInstance
            .get(`/api/agence/utilisateurs/code/${code}/${codeOrg}`)
            .then((resp) => {
                setUtilisateurs(resp.data);
                setLoadingUsers(false);
            })
            .catch((err) => {
                console.log(err);
            });

        axiosInstance
            .get(`/api/contrat/stats/agence/${code}/organisme/${codeOrg}`)
            .then((res) => {
                console.log("stats", res.data);
                setGlobalStats(res.data);
            })
            .catch((err) => {
                console.log(err);
            });

        axiosInstance
            .get('/api/parametre/villes')
            .then((res) => {
                const data = Array.isArray(res?.data) ? res.data : [];
                setVilles(data);
            })
            .catch((err) => {
                console.error("Erreur lors du chargement des villes:", err);
                setVilles([]);
            });
    }, [isModalVisible]);

    useEffect(() => {
        axiosInstance
            .get(`/api/categorie`)
            .then((res) => {
                const filtered = (res.data || []).filter((cat) => !cat.codeCategorie?.startsWith("V") && cat.codeCategorie !== "MO");
                setCategories(filtered);
            })
            .catch((err) => {
                console.log(err);
            });
    }, []);

    useEffect(() => {
        if (!categories.length) return;
        const tauxList =
            agence?.tauxCommission ||
            agence?.tauxCommissions ||
            (Array.isArray(agence?.validAgrements) && agence.validAgrements[0]?.tauxCommission) ||
            (Array.isArray(agence?.agrements) && agence.agrements[0]?.tauxCommission) ||
            [];
        setCommissionValues((prev) => {
            const mapped = { ...prev };
            categories.forEach((cat) => {
                const match = tauxList.find((tc) => tc?.categorie?.codeCategorie === cat.codeCategorie);
                if (match) {
                    mapped[cat.codeCategorie] = match.taux;
                } else if (mapped[cat.codeCategorie] === undefined) {
                    mapped[cat.codeCategorie] = 0;
                }
            });
            return mapped;
        });
    }, [categories, agence]);

    const fetchAgreement = async () => {
        if (!agence?.codeAgence) return null;
        try {
            setLoadingAgreement(true);
            const res = await axiosInstance.get(`/api/agence/code/${agence.codeAgence}`);
            const agData = res?.data || {};
            setAgence(agData);
            const agId = extractAgreementId(agData);
            setAgreementId(agId);
            return agId;
        } catch (error) {
            console.log(error);
            toast("Impossible de récupérer l'accord de l'agence");
            return null;
        } finally {
            setLoadingAgreement(false);
        }
    };

    const showActivateConfirm = () => {
        confirm({
            title: `Etes-vous sûr de vouloir ${agence?.restrictionProduction ? "activer" : "désactiver"} cette agence?`,
            icon: <ExclamationCircleFilled />,
            content: "Toutes vos actions sont historisées!",
            okText: "Oui",
            okType: "danger",
            cancelText: "Annuler",
            onOk() {
                activateAgency(agence);
            },
        });
    };

    const activateAgency = (agence) => {
        axiosInstance
            .get(`/api/agence/activate/${agence.codeId}`)
            .then((res) => {
                window.location.reload();
                toast(
                    agence?.restrictionProduction
                        ? "Agence activée avec succès"
                        : "Agence désactivée avec succès",
                );
            })
            .catch((err) => {
                console.log(err);
                toast("Erreur lors de la mise à jour");
            });
    };

    const handleSaveCommissionRates = async () => {
        if (!categories.length) return;
        setSavingTaux(true);
        const agId = agreementId || await fetchAgreement();
        if (!agId) {
            toast("Aucun accord n'a été trouvé pour cette agence.");
            setSavingTaux(false);
            return;
        }

        const requests = categories
            .filter((cat) => commissionValues[cat.codeCategorie] !== undefined)
            .map((cat) =>
                axiosInstance.get(
                    `/api/agreement/update/${agId}/${cat.codeCategorie}/${commissionValues[cat.codeCategorie] || 0}`
                )
            );

        Promise.all(requests)
            .then(() => {
                toast("Taux de commission mis à jour avec succès!");
                setManageTauxOpen(false);
                setAgence((prev) => {
                    if (!prev) return prev;
                    const updatedRates = categories
                        .filter((cat) => commissionValues[cat.codeCategorie] !== undefined)
                        .map((cat) => ({
                            categorie: cat,
                            taux: commissionValues[cat.codeCategorie] || 0,
                        }));
                    return { ...prev, tauxCommission: updatedRates, validAgrements: prev.validAgrements || [{ agreementId: agId }] };
                });
            })
            .catch((err) => {
                console.log(err);
                toast("Erreur lors de la mise à jour des taux de commission");
            })
            .finally(() => setSavingTaux(false));
    };

    const handleOpenManageRates = async () => {
        const agId = agreementId || await fetchAgreement();
        if (!agId) return;
        setManageTauxOpen(true);
    };

    return (
        <div className="layout-wrapper layout-content-navbar  ">
            <div className="layout-container">
                <SideBar />

                {/* <!-- Layout container --> */}
                <div className="layout-page">
                    <NavBar />

                    {/* <!-- Content wrapper --> */}
                    <div className="content-wrapper">
                        {/* <!-- Content --> */}
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <div style={{
                                background: BACKGROUND_LIGHT,
                                minHeight: '100vh',
                                padding: '24px'
                            }}>
                                {/* En-tête */}
                                <Card
                                    style={{
                                        borderRadius: '16px',
                                        marginBottom: '24px',
                                        boxShadow: '0 2px 8px rgba(36, 33, 102, 0.08)',
                                        border: 'none',
                                        background: `linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${SECONDARY_COLOR} 100%)`
                                    }}
                                    bodyStyle={{ padding: '32px' }}
                                >
                                    <Row justify="space-between" align="middle">
                                        <Col>
                                            <Space direction="vertical" size="small">
                                                <Badge
                                                    status={!agence?.restrictionProduction ? "success" : "error"}
                                                    text={
                                                        <Text style={{ color: 'white', fontSize: '14px' }}>
                                                            {!agence?.restrictionProduction ? 'Agence Active' : 'Agence Inactive'}
                                                        </Text>
                                                    }
                                                />
                                                <Title level={2} style={{ color: 'white', margin: 0 }}>
                                                    Code agence #{agence?.codeAgence}
                                                </Title>
                                                <Space>
                                                    <CalendarOutlined style={{ color: 'rgba(255,255,255,0.8)' }} />
                                                    <Text style={{ color: 'rgba(255,255,255,0.8)' }}>
                                                        Ajoutée le {frenchDate(agence?.createdAt || agence?.updatedAt)}
                                                    </Text>
                                                </Space>
                                            </Space>
                                        </Col>
                                        {canManageAgences && (
                                            <Col>
                                                <Space>
                                                    <Button
                                                        size="large"
                                                        icon={<EditOutlined />}
                                                        onClick={() => setIsModalVisible(true)}
                                                        style={{
                                                            borderRadius: '10px',
                                                            height: '44px',
                                                            fontWeight: 500,
                                                            backgroundColor: 'white',
                                                            color: PRIMARY_COLOR,
                                                            border: 'none'
                                                        }}
                                                    >
                                                        Modifier
                                                    </Button>
                                                    <Button
                                                        size="large"
                                                        danger
                                                        icon={<DeleteOutlined />}
                                                        style={{
                                                            borderRadius: '10px',
                                                            height: '44px',
                                                            fontWeight: 500
                                                        }}
                                                        onClick={() => showActivateConfirm()}
                                                    >
                                                        {!agence?.restrictionProduction ? 'Désactiver' : 'Activer'}
                                                    </Button>
                                                </Space>
                                            </Col>
                                        )}
                                    </Row>
                                </Card>

                                <Row gutter={[24, 24]}>
                                    {/* Colonne gauche - Informations */}
                                    <ProfilingAgence
                                        agence={agence}
                                        nbreContrats={globalStats?.totalContrats}
                                        montantPrimesMensuelles={globalStats?.totalPrimesMensuelles}
                                        logo={logoImagePreviewUrl}
                                        categories={categories}
                                        commissionValues={commissionValues}
                                        onManageRates={handleOpenManageRates}
                                        canManageRates={canManageAgences}
                                        loadingRates={loadingAgreement}
                                    />
                                    {/* Colonne droite - Contenu avec onglets */}
                                    <Col xs={24} lg={16}>
                                        <Card
                                            style={{
                                                borderRadius: '16px',
                                                boxShadow: '0 2px 8px rgba(36, 33, 102, 0.08)',
                                                border: 'none'
                                            }}
                                            bodyStyle={{ padding: 0 }}
                                        >
                                            <Tabs
                                                activeKey={activeTab}
                                                onChange={setActiveTab}
                                                style={{ padding: '0 32px' }}
                                                tabBarStyle={{
                                                    borderBottom: `2px solid ${PRIMARY_COLOR}15`,
                                                    marginBottom: 0
                                                }}
                                                items={[
                                                    {
                                                        key: '1',
                                                        label: (
                                                            <span style={{ fontSize: '15px', fontWeight: 500 }}>
                                                                <FileTextOutlined /> Aperçu
                                                            </span>
                                                        ),
                                                        children: (
                                                            <div style={{ padding: '32px' }}>
                                                                <AgenceStatsSummary agence={agence} montantPrimes={globalStats?.totalPrimes} montantPrimesMensuelles={globalStats?.totalPrimesMensuelles} />
                                                            </div>
                                                        )
                                                    },
                                                    {
                                                        key: '2',
                                                        label: (
                                                            <span style={{ fontSize: '15px', fontWeight: 500 }}>
                                                                <TeamOutlined /> Utilisateurs
                                                            </span>
                                                        ),
                                                        children: (
                                                            <div style={{ padding: '12px 5px' }}>
                                                                <UtilisateursAgenceTab utilisateurs={utilisateurs} loading={loadingUsers} />
                                                            </div>
                                                        )
                                                    },
                                                    {
                                                        key: 'suivi',
                                                        label: (
                                                            <span style={{ fontSize: '15px', fontWeight: 500 }}>
                                                                <DollarOutlined /> Suivi
                                                            </span>
                                                        ),
                                                        children: (
                                                            <SuiviAgenceTab
                                                                codeAgence={agence?.codeAgence}
                                                                codeOrganisme={agence?.organisme?.code}
                                                                nomAgence={agence?.nom}
                                                            />
                                                        )
                                                    },
                                                    {
                                                        key: '3',
                                                        label: (
                                                            <span style={{ fontSize: '15px', fontWeight: 500 }}>
                                                                <BellOutlined /> Notifications
                                                            </span>
                                                        ),
                                                        children: (
                                                            <div style={{ padding: '32px', textAlign: 'center', minHeight: '300px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                                                <BellOutlined style={{ fontSize: '64px', color: PRIMARY_COLOR, opacity: 0.3 }} />
                                                                <Title level={4} style={{ marginTop: '24px', color: PRIMARY_COLOR }}>
                                                                    Aucune notification
                                                                </Title>
                                                                <Text type="secondary">
                                                                    Les notifications apparaîtront ici
                                                                </Text>
                                                            </div>
                                                        )
                                                    }
                                                ]}
                                            />
                                        </Card>
                                    </Col>
                                </Row>
                                {/* Modal d'édition */}
                                <AddAgenceModal
                                    visible={isModalVisible}
                                    setVisible={setIsModalVisible}
                                    onCancel={() => setIsModalVisible(false)}
                                    villes={villes}
                                    membre={membre}
                                    mode='edit'
                                    agenceData={agence}
                                    onSaved={handleAgenceSaved}
                                />
                                <TauxCommissionsAgenceModal
                                    open={manageTauxOpen}
                                    onClose={() => setManageTauxOpen(false)}
                                    categories={categories}
                                    commissionValues={commissionValues}
                                    setCommissionValues={setCommissionValues}
                                    onSave={handleSaveCommissionRates}
                                    saving={savingTaux}
                                    canEditRates={canManageAgences}
                                    agence={agence}
                                />
                            </div>
                        </div>
                        {/* <!-- / Content --> */}

                        {/* <!-- Footer --> */}
                        <Footer />
                        {/* <!-- / Footer --> */}

                        <div className="content-backdrop fade"></div>
                    </div>
                    {/* <!-- Content wrapper --> */}
                </div>
                {/* <!-- / Layout page --> */}
            </div>

            {/* <!-- Overlay --> */}
            <div className="layout-overlay layout-menu-toggle"></div>

            {/* <!-- Drag Target Area To SlideIn Menu On Small Screens --> */}
            <div className="drag-target"></div>
        </div>
    );
};

export default DetailsAgence;
